import React, { useEffect, useState } from "react";
import moment from "moment";
import Str from "../../../common/Str";
import Checkbox from "../../../library/Checkbox";
import Pagination from "../../../library/Pagination";
import ModuleReadinessPopover from "./ModuleReadinessPopover";
import {
    ActionsCell,
    GradeCell,
    ProgrammeDetailsCell,
    ReadinessCell,
    SkeletonRow,
    VersionCell,
} from "./LinkedProgrammeRowCells";
import {
    StyledTableScroller,
    StyledTable,
    StyledCheckboxHeadCell,
    StyledProgrammeHeadCell,
    StyledVersionHeadCell,
    StyledIntakeHeadCell,
    StyledDateHeadCell,
    StyledEndDateHeadCell,
    StyledGradesHeadCell,
    StyledStudentsHeadCell,
    StyledActionsHeadCell,
    StyledCell,
    StyledCheckboxCell,
    StyledProgrammeCell,
    StyledStudentCell,
    StyledSortButton,
    StyledSortIcon,
    StyledHeaderIcon,
    StyledLoadingRow,
    StyledQaProgrammeHeadCell,
    StyledQaProgrammeCell,
    StyledQaVersionHeadCell,
    StyledQaVersionCell,
    StyledQaIntakeHeadCell,
    StyledQaIntakeCell,
    StyledReadinessHeadCell,
    StyledReadinessCell,
    StyledQaGradesHeadCell,
    StyledQaGradesCell,
    StyledQaStudentsHeadCell,
    StyledQaStudentsCell,
    StyledQaActionsHeadCell,
    StyledQaActionsCell,
} from "./LinkedProgrammesTable.styles";
import {
    TABLE_DATE_FORMAT,
    TABLE_ROWS_PER_PAGE,
} from "../../../../utils/Constants";

/**
 * Table of linked programmes (standard and QA views), with sorting, row
 * selection, grade lock/unlock, a module-readiness popover, and pagination
 * — all driven by the `hook` returned from the linked-programmes list hook.
 * @param {Object} hook State and handlers from the linked-programmes list hook.
 */
function LinkedProgrammesTable({
    hook,
}) {
    const [modulesRow, setModulesRow] = useState(null);
    const [modulesAnchor, setModulesAnchor] = useState(null);

    // Close the readiness popover on page/page-size change: paging away
    // unmounts the row button `modulesAnchor` points at, which otherwise
    // leaves the popover positioned against a detached DOM node.
    useEffect(() => {
        setModulesRow(null);
        setModulesAnchor(null);
    }, [hook.page, hook.perPage]);

    const renderSortHeader = (
        label,
        field,
        icon,
        ariaLabel
    ) => (
        <StyledSortButton
            type="button"
            onClick={() => hook.changeSort(field)}
            disabled={hook.isProcessing}
            aria-label={ariaLabel || undefined}
        >
            {icon && (
                <StyledHeaderIcon
                    className={`fal fa-${icon}`}
                    aria-hidden="true"
                />
            )}

            {label}

            <StyledSortIcon
                className="fal fa-angles-up-down"
                aria-hidden="true"
            />
        </StyledSortButton>
    );

    const handleToggleModules = (row, event) => {
        if (modulesRow?.id === row.id) {
            setModulesRow(null);
            setModulesAnchor(null);
            return;
        }

        setModulesRow(row);
        setModulesAnchor(event.currentTarget);
    };

    const columnCount =
        1 +
        (hook.isVersionEnabled ? 1 : 0) +
        (hook.QAview ? 6 : 7);

    return (
        <>
      <StyledTableScroller>
        <StyledTable $qaView={hook.QAview}>
          <thead>
            <tr>
              <StyledCheckboxHeadCell>
                <Checkbox
                                    checked={hook.allMatchingSelected}
                                    indeterminate={hook.someMatchingSelected}
                                    disabled={
                                        hook.isProcessing ||
                                        hook.loading ||
                                        hook.totalRows === 0
                                    }
                                    onChange={hook.toggleAllMatching}
                                    ariaLabel="Select all programmes matching current filters"
                                />
              </StyledCheckboxHeadCell>

              {hook.QAview ? (
                                <>
                  <StyledQaProgrammeHeadCell>
                    {renderSortHeader(
                                            "Programme Details",
                                            "name"
                                        )}
                  </StyledQaProgrammeHeadCell>

                  {hook.isVersionEnabled && (
                                        <StyledQaVersionHeadCell>
                      {renderSortHeader(
                                                "Version",
                                                "version_name"
                                            )}
                    </StyledQaVersionHeadCell>
                                    )}

                  <StyledQaIntakeHeadCell>
                    {renderSortHeader(
                                            "Intake",
                                            "intakes"
                                        )}
                  </StyledQaIntakeHeadCell>

                  <StyledReadinessHeadCell>
                    {renderSortHeader(
                                            "Module readiness",
                                            "module_ready"
                                        )}
                  </StyledReadinessHeadCell>

                  <StyledQaGradesHeadCell>
                    {renderSortHeader(
                                            "Grades",
                                            "isGradeLocked"
                                        )}
                  </StyledQaGradesHeadCell>

                  <StyledQaStudentsHeadCell>
                    {renderSortHeader(
                                            "",
                                            "total_student",
                                            "users",
                                            "Sort by students"
                                        )}
                  </StyledQaStudentsHeadCell>

                  <StyledQaActionsHeadCell>
                    Action
                  </StyledQaActionsHeadCell>
                </>
                            ) : (
                                <>
                  <StyledProgrammeHeadCell>
                    {renderSortHeader(
                                            "Programme Details",
                                            "name"
                                        )}
                  </StyledProgrammeHeadCell>

                  {hook.isVersionEnabled && (
                                        <StyledVersionHeadCell>
                      {renderSortHeader(
                                                "Version",
                                                "version_name"
                                            )}
                    </StyledVersionHeadCell>
                                    )}

                  <StyledIntakeHeadCell>
                    {renderSortHeader(
                                            "Intake",
                                            "intakes"
                                        )}
                  </StyledIntakeHeadCell>

                  <StyledDateHeadCell>
                    {renderSortHeader(
                                            "Start",
                                            "start",
                                            "calendar-alt"
                                        )}
                  </StyledDateHeadCell>

                  <StyledEndDateHeadCell>
                    {renderSortHeader(
                                            "End",
                                            "end",
                                            "calendar-alt"
                                        )}
                  </StyledEndDateHeadCell>

                  <StyledGradesHeadCell>
                    {renderSortHeader(
                                            "Grades",
                                            "isGradeLocked"
                                        )}
                  </StyledGradesHeadCell>

                  <StyledStudentsHeadCell>
                    {renderSortHeader(
                                            "",
                                            "total_student",
                                            "users",
                                            "Sort by students"
                                        )}
                  </StyledStudentsHeadCell>

                  <StyledActionsHeadCell>
                    Action
                  </StyledActionsHeadCell>
                </>
                            )}
            </tr>
          </thead>

          <tbody>
            {hook.loading ? (
                            Array(5)
                                .fill(null)
                                .map((skeletonPlaceholder, index) => (
                                    <tr key={`skeleton-${index}`}>
                                        <SkeletonRow
                                            QAview={hook.QAview}
                                            isVersionEnabled={hook.isVersionEnabled}
                                        />
                                    </tr>
                                ))
                        ) : hook.listData.length === 0 ? (
                            <tr>
                <StyledLoadingRow colSpan={columnCount}>
                  {Str.noRecord}
                </StyledLoadingRow>
              </tr>
                        ) : (
                            hook.listData.map((row) => (
                                <tr key={row?.id}>
                  <StyledCheckboxCell>
                    <Checkbox
                                            checked={hook.selectedIds.includes(
                                                row.id
                                            )}
                                            disabled={hook.isProcessing}
                                            onChange={() =>
                                                hook.toggleRow(row.id)
                                            }
                                            ariaLabel={`Select ${
                        row?.name || "programme"
                      }`}
                                        />
                  </StyledCheckboxCell>

                  {hook.QAview ? (
                                        <>
                      <StyledQaProgrammeCell>
                        <ProgrammeDetailsCell row={row} />
                      </StyledQaProgrammeCell>

                      {hook.isVersionEnabled && (
                                                <StyledQaVersionCell>
                          <VersionCell row={row} />
                        </StyledQaVersionCell>
                                            )}

                      <StyledQaIntakeCell>
                        {row?.intakes || "-"}
                      </StyledQaIntakeCell>

                      <StyledReadinessCell>
                        <ReadinessCell
                          row={row}
                          hook={hook}
                          modulesRow={modulesRow}
                          onToggleModules={handleToggleModules}
                        />
                      </StyledReadinessCell>

                      <StyledQaGradesCell>
                        <GradeCell row={row} hook={hook} />
                      </StyledQaGradesCell>

                      <StyledQaStudentsCell>
                        {row?.total_student ?? "-"}
                      </StyledQaStudentsCell>

                      <StyledQaActionsCell>
                        <ActionsCell row={row} hook={hook} />
                      </StyledQaActionsCell>
                    </>
                                    ) : (
                                        <>
                      <StyledProgrammeCell>
                        <ProgrammeDetailsCell row={row} />
                      </StyledProgrammeCell>

                      {hook.isVersionEnabled && (
                                                <StyledCell>
                          <VersionCell row={row} />
                        </StyledCell>
                                            )}

                      <StyledCell>
                        {row?.intakes || "-"}
                      </StyledCell>

                      <StyledCell>
                        {row?.start
                                                    ? moment
                                                            .unix(row.start)
                                                            .format(TABLE_DATE_FORMAT)
                                                    : "-"}
                      </StyledCell>

                      <StyledCell>
                        {row?.end
                                                    ? moment
                                                            .unix(row.end)
                                                            .format(TABLE_DATE_FORMAT)
                                                    : "-"}
                      </StyledCell>

                      <StyledCell>
                        <GradeCell row={row} hook={hook} />
                      </StyledCell>

                      <StyledStudentCell>
                        {row?.total_student ?? "-"}
                      </StyledStudentCell>

                      <StyledCell>
                        <ActionsCell row={row} hook={hook} />
                      </StyledCell>
                    </>
                                    )}
                </tr>
                            ))
                        )}
          </tbody>
        </StyledTable>
      </StyledTableScroller>

      {!hook.loading && hook.listData.length > 0 && (
                <Pagination
                    totalEntries={hook.totalRows}
                    rangeStart={hook.rangeStart}
                    rangeEnd={hook.rangeEnd}
                    rowsPerPage={hook.perPage}
                    rowsPerPageOptions={TABLE_ROWS_PER_PAGE}
                    onRowsPerPageChange={(value) => {
                        hook.setPerPage(value);
                        hook.setPage(1);
                    }}
                    currentPage={hook.page}
                    totalPages={hook.totalPages}
                    onPageChange={hook.setPage}
                    disabled={hook.isProcessing}
                    disabledReason="A bulk action is currently running"
                />
            )}

      <ModuleReadinessPopover
                row={modulesRow}
                pmcId={hook.id}
                anchorElement={modulesAnchor}
                onClose={() => {
                    const button = modulesAnchor;
                    setModulesRow(null);
                    setModulesAnchor(null);
                    window.setTimeout(
                        () => button?.focus(),
                        0
                    );
                }}
            />
    </>
    );
}

export default LinkedProgrammesTable;
